import { Component } from '@angular/core';
import { BaseChartComponent, IChartReportModel } from './base-chart.component';
import { ResponsiveService } from '@app/shared/responsive/responsive.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { ReportDataService } from '@app/shared/report/report-data.service';
import { StateService } from '@uirouter/angular';
import { ChartService, IChartReport, SECTION_CATEGORY_COUNT, SECTION_COUNTRY_REVENUE, SECTION_DATE_REVENUE, SECTION_MOM_REVENUE } from './chart.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { Override } from '@app/shared/app-decorators';
import { CHART_REPORT_ID } from './base-chart.component';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import _ from 'underscore';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IObject } from '@type_definitions/datatypes';
import { ReportGridService } from '@app/shared/report/report-grid.service';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { DashboardDataService } from '@app/shared/report/dashboard-data.service';
import { CurrencyService } from '@app/shared/services/currency.service';
import { CompanyType } from '@app/shared/company/company-data.service';

@Component({
  selector: 'chart-customer',
  templateUrl: './base-chart.component.html'
})
export class ChartCustomerComponent extends BaseChartComponent {
  private dateReport:IChartReport;
  private countryReport:IChartReport;
  private categoryReport:IChartReport;
  private momRevenueReport:IChartReport;
  private customerSelectField:IExtraFieldConfigurationObject;
  private webshopSelectField:IExtraFieldConfigurationObject;
  private testOrderField:IExtraFieldConfigurationObject;
  private defaultCustomer:number;
  
  constructor(
    responsiveService:ResponsiveService,
    authService:AuthService,
    gaStateService:GaStateService,
    formlyService:GaFormlyService,
    reportFiltersUtil:ReportFiltersUtil,
    reportDataService:ReportDataService,
    stateService:StateService,
    chartService:ChartService,
    reportGridService:ReportGridService,
    currencyService:CurrencyService,
    formattingService:FormattingService,
    messageService:GaMessageService,
    private translationService:TranslationService,
    private dashboardDataService:DashboardDataService
  ) {
    super(responsiveService, authService, gaStateService, formlyService, reportFiltersUtil, reportDataService, stateService, chartService, reportGridService, currencyService, formattingService, messageService);
  }

  @Override(BaseChartComponent)
  public chartOnInit():void {
    const currentUser:IUserData = this.authService.getCurrentUser();
    const isCustomerUser:boolean = currentUser.location?.companyCountry?.company?.companyType === CompanyType.CUSTOMER;
    const userCompanyId:number = currentUser.location?.companyCountry?.company?.id;

    this.defaultCustomer = isCustomerUser? userCompanyId : undefined;
    this.model = this.paramsToModel(this.stateService.params);

    this.dateReport = {
      id: SECTION_DATE_REVENUE,
      title: this.getSectionTitle(SECTION_DATE_REVENUE),
      collapsed: true,
      params: this.createReportParams()
    };

    this.countryReport = {
      id: SECTION_COUNTRY_REVENUE,
      title: this.getSectionTitle(SECTION_COUNTRY_REVENUE),
      collapsed: true,
      params: this.createReportParams()
    };

    this.categoryReport = {
      id: SECTION_CATEGORY_COUNT,
      title: this.getSectionTitle(SECTION_CATEGORY_COUNT),
      collapsed: true,
      params: this.createReportParams()
    };

    this.momRevenueReport = {
      id: SECTION_MOM_REVENUE,
      title: this.getSectionTitle(SECTION_MOM_REVENUE),
      collapsed: false,
      params: this.createReportParams()
    };

    this.chartReports = [
      this.momRevenueReport,
      this.dateReport,
      this.countryReport,
      this.categoryReport
    ];

    this.fields = this.createFields(isCustomerUser);
  }

  @Override(BaseChartComponent)
  public fillFields():void {
    if (this.customerSelectField) {
      // find customer column id
      const customerColumnId:string = _.findWhere(this.reportMetaData.columns, { name: 'Customer' }).id;
      // load list of customers
      this.formlyService.fillField(this.customerSelectField, this.reportDataService.loadListFilterOptions(CHART_REPORT_ID, customerColumnId));
    }

    const webshopColumnId:string = _.findWhere(this.reportMetaData.columns, { name: 'Webshop' }).id;
    // load list of webshops
    this.formlyService.fillField(this.webshopSelectField, this.reportDataService.loadListFilterOptions(CHART_REPORT_ID, webshopColumnId));
    // load list of yes/no (test order)
    this.formlyService.fillField(this.testOrderField, this.dashboardDataService.yesNo());
  }

  @Override(BaseChartComponent)
  public createReports():void {
    this.dateReport = _.extend(this.dateReport, this.createDateReport());
    this.countryReport = _.extend(this.countryReport, this.createCountryReport());
    this.categoryReport = _.extend(this.categoryReport, this.createCategoryReport());
    this.momRevenueReport = _.extend(this.momRevenueReport, this.createMoMRevenueReport());
  }

  @Override(BaseChartComponent)
  public validateFilters(doNotShowValidationErrors?:boolean):boolean {
    if (!this.model.customer && !this.model.webshop) {
      if (!doNotShowValidationErrors) {
        this.messageService.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), this.translationService.instant('CHART.VALIDATION.REQUIRED_CUSTOMER_WEBSHOP'));
      }

      return false;
    }

    return true;
  }

  @Override(BaseChartComponent)
  public paramsEqual(p1:IObject, p2:IObject):boolean {
    const paramNames = [
      'from',
      'to',
      'customer',
      'currency',
      'webshop',
      'testOrder'
    ];

    for (const paramName of paramNames) {
      if (((p1 && p1[ paramName ]) || undefined) !== ((p2 && p2[ paramName ]) || undefined)) {
        return false;
      }
    }

    return true;
  }

  @Override(BaseChartComponent)
  public modelToParams():IObject {
    const from:string = _.isDate(this.model.orderDate_FROM)? this.formattingService.formatDate(this.model.orderDate_FROM) : this.model.orderDate_FROM;
    const to:string = _.isDate(this.model.orderDate_TO)? this.formattingService.formatDate(this.model.orderDate_TO) : this.model.orderDate_TO;

    return {
      customer: this.model.customer !== this.defaultCustomer? this.model.customer : undefined,
      webshop: this.model.webshop,
      currency: this.model.currency !== this.defaultCurrency? this.model.currency : undefined,
      from: from !== this.defaultFromDate? from : undefined,
      to,
      testOrder: this.model.testOrder
    };
  }

  @Override(BaseChartComponent)
  public paramsToModel(params:any):IChartReportModel {
    const from:Date = this.chartService.getDateRangeValue(params.from);
    const to:Date = this.chartService.getDateRangeValue(params.to);

    return {
      customer: params.customer || this.defaultCustomer,
      webshop: params.webshop,
      currency: params.currency || this.defaultCurrency,
      orderDate_FROM: from || this.defaultFromDate,
      orderDate_TO: to,
      testOrder: params.testOrder || '0'
    };
  }

  private createFields(isCustomerUser:boolean):IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [
      this.webshopSelectField = this.formlyService.aSelect('webshop', 'GA.GENERAL.WEBSHOP')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withLabelProp('value')
        .withValueProp('id')
        .onChange(this.onModelChange)
        .build(),
      this.createCurrencyField(),
      this.createOrderDateFields(),
      this.testOrderField = this.formlyService.aSelect('testOrder', 'Test Order')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withLabelProp('value')
        .withValueProp('id')
        .onChange(this.onModelChange)
        .build()
    ];

    if (!isCustomerUser) {
      fields.unshift(
        this.customerSelectField = this.formlyService.aSelect('customer', 'GA.GENERAL.CUSTOMER')
          .withLabelClass(this.labelClass)
          .withValueClass(this.valueClass)
          .withLabelProp('value')
          .withValueProp('id')
          .onChange(this.onModelChange)
          .build()
      );
    }

    return fields;
  }
}